/**
 * Local-first sync outbox — persisted to `localStorage` so queued writes
 * survive a reload/offline stretch. `enqueue` merges into whatever is
 * already queued (never replaces it wholesale); `peek` hands the sync
 * engine an immutable snapshot to send; `clear` removes only the rows that
 * were actually applied, so anything enqueued while the request was in
 * flight survives untouched.
 *
 * Mirrors the server's row shapes exactly (see
 * `@repo/hindi-backend`'s `syncPushInput`) so a `peek()` payload can be
 * handed to `syncPush` verbatim.
 */

const STORAGE_KEY = "hindi.outbox.v1";

export interface SyncReviewRow {
  itemId: string;
  kind: string;
  ease: number;
  intervalDays: number;
  reps: number;
  lapses: number;
  dueAt: number;
  updatedAt: number;
}

export interface SyncLessonRow {
  lessonId: string;
  completedAt: number;
}

export interface SessionRow {
  id: string;
  dateKey: string;
  startedAt: number;
  durationSec: number;
  accuracy: number;
}

export interface SyncSettingsRow {
  json: string;
  updatedAt: number;
}

export interface OutboxPayload {
  reviews: SyncReviewRow[];
  lessons: SyncLessonRow[];
  sessions: SessionRow[];
  settings?: SyncSettingsRow;
}

function emptyPayload(): OutboxPayload {
  return { reviews: [], lessons: [], sessions: [] };
}

/** Narrow, defensive shape check — anything short of this shape is discarded. */
function isOutboxPayload(value: unknown): value is OutboxPayload {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate["reviews"]) &&
    Array.isArray(candidate["lessons"]) &&
    Array.isArray(candidate["sessions"])
  );
}

function readRaw(): OutboxPayload {
  if (typeof window === "undefined") return emptyPayload();
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return emptyPayload();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isOutboxPayload(parsed)) return emptyPayload();
    return parsed;
  } catch {
    return emptyPayload();
  }
}

function writeRaw(payload: OutboxPayload): void {
  if (typeof window === "undefined") return;
  const isEmpty =
    payload.reviews.length === 0 &&
    payload.lessons.length === 0 &&
    payload.sessions.length === 0 &&
    payload.settings === undefined;
  if (isEmpty) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

function mergeReviews(
  existing: SyncReviewRow[],
  incoming: SyncReviewRow[],
): SyncReviewRow[] {
  const byId = new Map(existing.map((row) => [row.itemId, row]));
  for (const row of incoming) {
    const current = byId.get(row.itemId);
    if (current === undefined || row.updatedAt >= current.updatedAt) {
      byId.set(row.itemId, row);
    }
  }
  return [...byId.values()];
}

function mergeLessons(
  existing: SyncLessonRow[],
  incoming: SyncLessonRow[],
): SyncLessonRow[] {
  const byId = new Map(existing.map((row) => [row.lessonId, row]));
  for (const row of incoming) {
    if (!byId.has(row.lessonId)) {
      byId.set(row.lessonId, row);
    }
  }
  return [...byId.values()];
}

function mergeSessions(
  existing: SessionRow[],
  incoming: SessionRow[],
): SessionRow[] {
  const byId = new Map(existing.map((row) => [row.id, row]));
  for (const row of incoming) {
    if (!byId.has(row.id)) {
      byId.set(row.id, row);
    }
  }
  return [...byId.values()];
}

function mergeSettings(
  existing: SyncSettingsRow | undefined,
  incoming: SyncSettingsRow | undefined,
): SyncSettingsRow | undefined {
  if (incoming === undefined) return existing;
  if (existing === undefined) return incoming;
  return incoming.updatedAt >= existing.updatedAt ? incoming : existing;
}

/** Merge `partial` into whatever is already queued. Keyed merges only — never a wholesale replace. */
export function enqueue(partial: Partial<OutboxPayload>): void {
  const current = readRaw();
  const next: OutboxPayload = {
    reviews: mergeReviews(current.reviews, partial.reviews ?? []),
    lessons: mergeLessons(current.lessons, partial.lessons ?? []),
    sessions: mergeSessions(current.sessions, partial.sessions ?? []),
    settings: mergeSettings(current.settings, partial.settings),
  };
  writeRaw(next);
}

/** The full queued payload, or `null` if nothing is queued. */
export function peek(): OutboxPayload | null {
  const payload = readRaw();
  const isEmpty =
    payload.reviews.length === 0 &&
    payload.lessons.length === 0 &&
    payload.sessions.length === 0 &&
    payload.settings === undefined;
  return isEmpty ? null : payload;
}

/**
 * Remove only the rows in `applied` from the queue — anything enqueued
 * after `applied` was captured (e.g. mid-flight, while the sync request
 * was in the air) survives untouched.
 *
 * Reviews compare by `itemId` + `updatedAt`: a queued row is dropped only
 * if its `updatedAt` is `<=` the applied row's `updatedAt` (a newer
 * mid-flight grade for the same item is kept). Lessons/sessions compare
 * by id alone (idempotent, no LWW). Settings is cleared only if the
 * queued settings row's `updatedAt` is `<=` the applied row's.
 */
export function clear(applied: OutboxPayload): void {
  const current = readRaw();

  const appliedReviewByIdMaxUpdatedAt = new Map(
    applied.reviews.map((row) => [row.itemId, row.updatedAt]),
  );
  const remainingReviews = current.reviews.filter((row) => {
    const appliedAt = appliedReviewByIdMaxUpdatedAt.get(row.itemId);
    return appliedAt === undefined || row.updatedAt > appliedAt;
  });

  const appliedLessonIds = new Set(applied.lessons.map((l) => l.lessonId));
  const remainingLessons = current.lessons.filter(
    (row) => !appliedLessonIds.has(row.lessonId),
  );

  const appliedSessionIds = new Set(applied.sessions.map((s) => s.id));
  const remainingSessions = current.sessions.filter(
    (row) => !appliedSessionIds.has(row.id),
  );

  let remainingSettings = current.settings;
  if (
    applied.settings !== undefined &&
    current.settings !== undefined &&
    current.settings.updatedAt <= applied.settings.updatedAt
  ) {
    remainingSettings = undefined;
  }

  writeRaw({
    reviews: remainingReviews,
    lessons: remainingLessons,
    sessions: remainingSessions,
    settings: remainingSettings,
  });
}
